import axios from "axios";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    useTheme,
    Tooltip
} from "@mui/material";
import {
    Datagrid,
    DateField,
    FunctionField,
    Link,
    NumberField,
    ReferenceField,
    TextField
} from "react-admin";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { AppBar, TitlePortal } from 'react-admin';
import { Title } from 'react-admin';


const API_URL = process.env.REACT_APP_API_URL;
const KH_LINK = process.env.REACT_APP_KH_LINK;
const TrainingDatagrid = ({data}) => {
    const theme = useTheme();
    return(
    <Box sx={{ overflowX: 'auto' }}>
        <Datagrid
            data={data}
            // rowClick={(id) => onRowClick(id)}
            sort={{ field: 'start_dt', order: 'ASC' }}
            sx={{
                '& .MuiTable-root': {
                    tableLayout: 'fixed',
                    minWidth: '1500px', // ⬅️ give the table enough base width
                },
                '& .RaDatagrid-headerCell': {
                    backgroundColor: '#f2f2f7',
                    fontWeight: 'bold',
                },
                '& .MuiTableCell-root': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'top',
                },

                // column width definitions (works for <th> and <td>)
                '& .column-locator': { width: 80 },
                '& .column-reference': { width: 300 },
                '& .column-room': { width: 100 },
                '& .column-rule': { width: 80 },
                '& .column-trainer': { width: 200 },
                '& .column-seats': { width: 110 },
                '& .column-pods': { width: 110 },
                '& .column-tz': { width: 100 },
                '& .column-start': { width: 160 },
                '& .column-end': { width: 160 },
                '& .column-flag': { width: 80, textAlign: 'center' },
            }}
        >   
        
            <FunctionField
                label="Locator" 
                // sortBy="locator"
                headerClassName="column-locator"
                cellClassName="column-locator"
                render={record =>
                    record?.locator ? (
                        <a href={`${KH_LINK}${record.locator}`} target="_blank" style={{color:theme.palette.primary.main,textDecoration: 'none'}}>
                            {record.locator}
                        </a>
                    ) : (
                        "-"
                    )
                }
            />
            <FunctionField
                        label="Session ID"
                        // sortBy="reference"
                        headerClassName="column-reference"
                        cellClassName="column-reference"
                        render={record =>
                            record?.reference ? (
                                <Link
                                    to={`/training/${record.id}/show`}
                                    style={{
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    {record.reference}
                                </Link>
                            ) : (
                                "-"
                            )
                        }
                    />
            <ReferenceField
                label="Room"
                source="room_id"
                reference="rlab/room"
                link="show"
                emptyText="-"
                headerClassName="column-room"
                cellClassName="column-room"
            >
                <TextField source="name" />
            </ReferenceField>
            <ReferenceField
                label="Rule"
                source="rule_id"
                reference="rule"
                link="show"
                emptyText="-"
                headerClassName="column-rule"
                cellClassName="column-rule"
            >
                <TextField source="id" />
            </ReferenceField>

            {/* Trainer with tooltip and multi-line wrap */}
            <FunctionField
                label="Trainer"
                headerClassName="column-trainer"
                cellClassName="column-trainer"
                render={(record) => {
                    const trainers =
                        record.trainers?.length
                            ? record.trainers.map(t => `${t.firstname} ${t.lastname}`).join(', ')
                            : '-';
                    return (
                        <Tooltip title={trainers}>
                            <span
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                }}
                            >
                                {trainers}
                            </span>
                        </Tooltip>
                    );
                }}
            />

            <NumberField
                label="Nb Trainees"
                source="seats"
                emptyText="-"
                headerClassName="column-seats"
                cellClassName="column-seats"
            />
            <NumberField
                label="Nb Pods"
                source="realPodCount"
                emptyText="-"
                headerClassName="column-pods"
                cellClassName="column-pods"
            />
            <TextField
                label="TimeZone"
                source="time_zone"
                emptyText="-"
                headerClassName="column-tz"
                cellClassName="column-tz"
            />
            <DateField
                label="Start Date"
                source="start_dt"
                showTime
                emptyText="-"
                headerClassName="column-start"
                cellClassName="column-start"
            />
            <DateField
                label="End Date"
                source="end_dt"
                showTime
                emptyText="-"
                headerClassName="column-end"
                cellClassName="column-end"
            />

            <FunctionField
                label="Check"
                headerClassName="column-flag"
                cellClassName="column-flag"
                render={(r) =>
                    r.was_checked === null ? "-" : r.was_checked ? "✅" : "❌"
                }
            />

            <FunctionField
                label="Deploy"
                headerClassName="column-flag"
                cellClassName="column-flag"
                render={(r) =>
                    r.is_deployed === null ? "-" : r.is_deployed ? "✅" : "❌"
                }
            />

            <FunctionField
                label="Clean"
                headerClassName="column-flag"
                cellClassName="column-flag"
                render={(r) =>
                    r.is_archived === null ? "-" : r.is_archived ? "✅" : "❌"
                }
            />
        </Datagrid>
    </Box>
    )
};

const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
};

// Fetch trainings upcoming in next 7 days
const fetchTrainingsForUpcoming7Days = async () => {
    const res = await axios.get(`${API_URL}/training/upcoming?days=7`, {
        headers: getAuthHeaders(),
    });
    return res.data.data;
};

// Fetch ongoing trainings
const fetchOngoingTrainings = async () => {
    const res = await axios.get(`${API_URL}/training/ongoing`, {
        headers: getAuthHeaders(),
    });
    return res.data.data;
};

// Fetch classrooms
const fetchClassrooms = async () => {
    const res = await axios.get(`${API_URL}/rlab/room`, {
        headers: getAuthHeaders(),
    });
    return res.data.data;
};

const Dashboard = () => {
    const navigate = useNavigate();

    // Queries
    const { data: trainings = [], isLoading, error } = useQuery(
        ["trainings", "upcoming"],
        fetchTrainingsForUpcoming7Days
    );

    const { data: trainings_ongoing = [], isLoadingO, errorO } = useQuery(
        ["trainings", "ongoing"],
        fetchOngoingTrainings
    );

    const { data: allrooms = [], isLoadingR, errorR } = useQuery(
        ["rlab/room"],
        fetchClassrooms
    );

    // Loading / error states
    if (  isLoading || isLoadingR || isLoadingO ) {
        return <div>Loading...</div>;
    }

    if (error || errorR || errorO ) {
        return <div>Error: {error.message || errorR?.message || errorO?.message }</div>;
    }

    const handleRowClick = (id) => {
        navigate(`/training/${id}/show`);
    };

    return (
                
        <Card variant="outlined" sx={{ minWidth: 275, boxShadow: 3 }}>
                    <Title title="Dashboard" />
            {/* Upcoming trainings */}
            <CardContent>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        sx={{
                            fontWeight: "bold",
                            color: "#1976d2",
                            textAlign: "left",
                            textTransform: "uppercase"
                        }}
                    >
                        UPCOMING SESSIONS IN THE NEXT 7 DAYS
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Box>
                {trainings.length > 0 ? (
                    <TrainingDatagrid data={trainings}  />
                ) : (
                    <Typography>No Deployments scheduled for the next 7 days.</Typography>
                )}
            </CardContent>

            {/* Ongoing trainings */}
            <CardContent>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        sx={{
                            fontWeight: "bold",
                            color: "#1976d2",
                            textAlign: "left",
                            textTransform: "uppercase"
                        }}
                    >
                        ONGOING SESSIONS
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Box>
                {trainings_ongoing.length > 0 ? (
                    <TrainingDatagrid data={trainings_ongoing} />
                ) : (
                    <Typography>No ongoing sessions at the moment.</Typography>
                )}
            </CardContent>

            {/* Classrooms */}
            <CardContent>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        sx={{
                            fontWeight: "bold",
                            color: "#1976d3",
                            textAlign: "left",
                            textTransform: "uppercase"
                        }}    
        >
                        Available Classrooms
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Box>
                {allrooms.length > 0 ? (
                    <Datagrid data={allrooms} sort={{ field: "number", order: "ASC" }}>
                        {/* Room info */}
                        <TextField label="Name" source="name" emptyText="-" />
                        <TextField label="Description" source="description" emptyText="-" />

                        {/* In use (training assigned or not) */}
                        <FunctionField
                            label="In use"
                            render={record =>
                                record.training ? "✅" : "❌"
                            }
                        />

                        {/* Training template */}
                        <TextField
                            label="Training template"
                            source="training_template_name"
                            emptyText="-"
                        />

                        <FunctionField
                            label="Trainer"
                            render={record =>
                                record.training_trainers && record.training_trainers.length > 0
                                    ? record.training_trainers
                                        .map(t => `${t.firstname} ${t.lastname}`)
                                        .join(", ")
                                    : "-"
                            }
                        />

                        <TextField label="Locator" source="training_locator" emptyText="-" />

                        {/* Training info */}
                        <FunctionField
                            label="Session ID"
                            render={record => record.training_title || "-"}
                            sx={{
                                maxWidth: 250,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        />
                    </Datagrid>
                ) : (
                    <Typography>No classrooms available.</Typography>
                )}


            </CardContent>
        </Card>
    );
};



export default Dashboard;
